import { Injectable, inject } from '@angular/core';
import { LIB_CONFIG, LibConfig } from '../lib.config';

enum Severity {
  error,
  warning,
  info,
  debug,
  trace
}

class LogMessage {
  public text?: string;
  public timestamp?: string;
  public caller?: string;
  public severity?: Severity;

  public toString(): string {
    let callerText = '';
    if (this.caller) {
      callerText = this.caller + ': ';
    }
    return '[' + this.timestamp + ']\t ' + callerText + this.text;
  }
}

/**
 * Erweiterung und Wrapper,
 * um in die Entwicklung zu unterstützen mit logging-Code, der im Artefakt verweilt (statt console.log).

 * Kann auch spätere (Bug-)Analysen unterstützen, wenn dann Logging aktiviert und LogLevel eingestellt wird.
 * Ferner wäre eine Einbindung in den Konsumenten eine gute Vorarbeit,
 * wenn man später die Client-Logs auch an einen Server-Endpunkt schicken wollte.
 *
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private readonly logLevel: string = 'off';

  constructor() {
    const config = inject<LibConfig>(LIB_CONFIG);

    this.logLevel = config.logLevel || 'off';
  }

  // as we designed this to potentially log to console this is intentionally
  // tslint disabled by CKU, service implemented by TWE

  public trace(text: any, caller?: object) {
    const message = this.newLogMessage(text, Severity.trace, caller);
    if (['trace'].includes(this.logLevel.toLocaleLowerCase())) {
      this.handleLogMessage(message);
    }
  }

  public debug(text: any, caller?: object) {
    const message = this.newLogMessage(text, Severity.debug, caller);
    if (['trace', 'debug'].includes(this.logLevel.toLocaleLowerCase())) {
      this.handleLogMessage(message);
    }
  }

  public info(text: any, caller?: object) {
    const message = this.newLogMessage(text, Severity.info, caller);
    if (['trace', 'debug', 'info'].includes(this.logLevel.toLocaleLowerCase())) {
      this.handleLogMessage(message);
    }
  }

  public warn(text: any, caller?: object) {
    const message = this.newLogMessage(text, Severity.warning, caller);
    if (['trace', 'debug', 'info', 'warn'].includes(this.logLevel.toLocaleLowerCase())) {
      this.handleLogMessage(message);
    }
  }

  public error(text: any, caller?: object) {
    const message = this.newLogMessage(text, Severity.error, caller);
    if (['trace', 'debug', 'info', 'warn', 'error'].includes(this.logLevel.toLocaleLowerCase())) {
      this.handleLogMessage(message);
    }
  }

  private newLogMessage(text: any, severity: Severity, caller?: object): LogMessage {
    const message = new LogMessage();
    message.timestamp = new Date().toLocaleString();
    message.text = JSON.stringify(text);
    message.severity = severity;
    if (caller) {
      message.caller = caller.constructor.name;
    }

    return message;
  }

  private handleLogMessage(message: LogMessage) {
    if (this.logLevel.toLocaleLowerCase() !== 'off') {
      if (this.detectIE()) {
        this.printToConsole(message);
      } else {
        this.printToConsoleColored(message);
      }
    }
  }

  private printToConsoleColored(message: LogMessage) {
    const log = message.toString();

    if (message.severity === Severity.trace) {
      console.info('[trace]\t' + '%c' + log, 'color: darkgray');
    } else if (message.severity === Severity.debug) {
      console.info('[debug]\t' + '%c' + log, 'color: gray');
    } else if (message.severity === Severity.info) {
      console.info('[info]\t' + '%c' + log, 'color: blue');
    } else if (message.severity === Severity.warning) {
      console.info('[warn]\t' + '%c' + log, 'color: orange; background-color: lightgray');
    } else if (message.severity === Severity.error) {
      console.info('[error]\t' + '%c' + log, 'color: red; background-color: lightgray');
    }
  }

  private printToConsole(message: LogMessage) {
    const log = message.toString();

    if (message.severity === Severity.trace) {
      console.info('[trace]\t' + log);
    } else if (message.severity === Severity.debug) {
      console.info('[debug]\t' + log);
    } else if (message.severity === Severity.info) {
      console.info('[info]\t' + log);
    } else if (message.severity === Severity.warning) {
      console.info('[warn]\t' + log);
    } else if (message.severity === Severity.error) {
      console.info('[error]\t' + log);
    }
  }

  private detectIE(): boolean {
    const ua = window.navigator.userAgent;
    return ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0 || ua.indexOf('Edge/') > 0;
  }
}
